import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import React from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';

import type { GetUserQueryGQLType } from 'types/dashboard-core-gqlTypes.generated.d';

import { FundingRequestsHistoryRoute } from 'borrower/routes/funding-requests/FundingRequestsHistoryRoute';
import { FundingRequestsLandingRoute } from 'borrower/routes/funding-requests/FundingRequestsLandingRoute';
import { FundingRequestsRoute } from 'borrower/routes/funding-requests/FundingRequestsRoute';
import { useOrganizationUiVisibility } from 'packages/common-frontend/hooks/useUiVisibility';
import { FundingRequestHistoryWithVerificationRoute } from 'packages/funding-requests/routes/FundingRequestHistoryWithVerificationRoute';
import { VerificationEligibilityRoute } from 'packages/funding-requests/routes/VerificationEligibilityRoute';
import { VerificationHistoryRoute } from 'packages/funding-requests/routes/VerificationHistoryRoute';
import { VerificationWorkflowRoute } from 'packages/funding-requests/routes/VerificationWorkflowRoute';
import { baseNewVerificationUrl } from 'packages/verifications/ui/VerificationsTemplate';

export const FundingRequestRouter: React.FC<{
  userData: GetUserQueryGQLType;
}> = withLDConsumer()(({ userData, flags }) => {
  // @JonAtFinley @danitian replace with real UiVisibility returned from auth
  const organizationVisibility = useOrganizationUiVisibility({
    mockUiVisibilityMode: flags?.isBorrowerLimitedOrganization
      ? 'borrowerLimited'
      : 'borrowerOwner',
    organizationSlug: userData.user?.organization.slug,
  });

  const { fundingRequest, verification } = organizationVisibility;

  return (
    <Switch>
      {/* --- Funding request landing route --- */}
      <Route exact path="/funding-requests">
        {fundingRequest.showFundingRequestWorkflow ? (
          <FundingRequestsLandingRoute />
        ) : (
          <FundingRequestHistoryWithVerificationRoute
            enableVerifications={
              verification.showInitiateVerification &&
              verification.showVerificationPipelines
            }
          />
        )}
      </Route>

      {/* --- Funding verification routes --- */}
      {verification.showInitiateVerification && (
        <Route
          exact
          path={baseNewVerificationUrl}
          component={VerificationWorkflowRoute}
        />
      )}
      {verification.showVerificationPipelines && (
        <Route
          exact
          path="/funding-requests/verifications/pipeline/history"
          component={VerificationHistoryRoute}
        />
      )}
      {verification.showVerificationPipelines && (
        <Route
          exact
          path="/funding-requests/verifications/pipeline/:artifactDocumentId/:creditFacilityId/:reportingDate/:fileVersion"
          component={VerificationWorkflowRoute}
        />
      )}
      {verification.showVerificationPipelines && (
        <Route
          exact
          path="/funding-requests/verifications/pipeline/:artifactDocumentId/:creditFacilityId/:reportingDate/:fileVersion/:verificationReportId/eligibility"
          component={VerificationEligibilityRoute}
        />
      )}

      {/* --- Funding request workflow routes --- */}
      {fundingRequest.showFundingRequestWorkflow && (
        <Route
          exact
          path="/funding-requests/history"
          component={FundingRequestsHistoryRoute}
        />
      )}

      {/* --- Individual funding request routes --- */}
      <Route
        exact
        path="/funding-requests/:fundingRequestId"
        component={FundingRequestsRoute}
      />
      <Redirect
        from="/funding-requests/:fundingRequestId/approve"
        to="/funding-requests/:fundingRequestId"
      />
    </Switch>
  );
});
